import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";
import type {
	BackendEntityType,
	CreatePaymentRequestObject,
	OrderStatus,
	PaginationParamsModel,
	PaginationTokenType,
	PartialPaymentModel,
	PaymentAccountMetricsModel,
	PaymentRequestModel,
	PaymentRequestOrderModel,
	PendingPaymentRequestOrderModel,
	ShortPaymentTransactionModel
} from "shared/types";
import {
	paginationTokenResolver,
	paymentAccountMetricsResolver,
	paymentRequestOrderResolver,
	paymentRequestResolver,
	pendingPaymentRequestOrderResolver,
	shortPaymentTransactionResolver
} from "shared/types";

import BaseConverter from "./converters/baseConverter";

import type {
	ListPaymentOrgInfoResponse,
	PaymentRequestOrderBaseModal,
	StripeConnectAccountIntegrationResponse
} from "../types/PaymentModel";
import {
	listPaymentOrgInfoResolver,
	paymentRequestOrderBaseResolver,
	stripeConnectAccountIntegrationResponseResolver
} from "../types/PaymentModel";

const useSoccerPaymentApiService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			createPaymentRequest: async (data: CreatePaymentRequestObject) => {
				return BaseConverter.convert(await actions.post(API_URLS.SOCCER.CREATE_PAYMENT_REQUEST, data));
			},
			getPaymentRequests: async (
				params: {
					objectId: string;
					objectType: BackendEntityType;
					linkedObjectId?: string;
				} & PaginationParamsModel
			) => {
				return BaseConverter.convert<{
					nextToken: PaginationTokenType;
					paymentRequests: PaymentRequestModel[];
				}>(
					await actions.get(API_URLS.SOCCER.LIST_PAYMENT_REQUESTS, params, {
						resolvers: [
							{ resolver: paymentRequestResolver, fields: ["paymentRequests"] },
							{
								resolver: paginationTokenResolver,
								fields: ["nextToken"]
							}
						]
					})
				);
			},
			getPaymentRequestOrders: async (
				params: {
					paymentRequestId: string;
					search?: string;
					status?: OrderStatus[];
				} & PaginationParamsModel
			) => {
				return BaseConverter.convert<{
					nextToken: PaginationTokenType;
					orders: PaymentRequestOrderModel[];
				}>(
					await actions.get(
						API_URLS.SOCCER.LIST_PAYMENT_REQUEST_ORDERS,
						{
							...params,
							status: params?.status?.length ? params.status.flat().join(",") : []
						},
						{
							resolvers: [
								{
									resolver: paymentRequestOrderResolver,
									fields: ["orders"]
								},
								{
									resolver: paginationTokenResolver,
									fields: ["nextToken"]
								}
							]
						}
					)
				);
			},
			getPaymentHistory: async (params: PaginationParamsModel) => {
				return BaseConverter.convert<{
					nextToken: PaginationTokenType;
					transactions: ShortPaymentTransactionModel[];
				}>(
					await actions.get(API_URLS.SOCCER.LIST_PAYMENTS_HISTORY, params, {
						resolvers: [
							{
								resolver: shortPaymentTransactionResolver,
								fields: ["transactions"]
							},
							{
								resolver: paginationTokenResolver,
								fields: ["nextToken"]
							}
						]
					})
				);
			},
			getTransactionsHistory: async (params: PaginationParamsModel) => {
				return BaseConverter.convert<{
					nextToken: PaginationTokenType;
					transactions: ShortPaymentTransactionModel[];
				}>(
					await actions.get(API_URLS.SOCCER.LIST_TRANSACTIONS_HISTORY, params, {
						resolvers: [
							{
								resolver: shortPaymentTransactionResolver,
								fields: ["transactions"]
							},
							{
								resolver: paginationTokenResolver,
								fields: ["nextToken"]
							}
						]
					})
				);
			},
			getPaymentAccountMetrics: async (params: { objectId: string; startsAt?: Date; endsAt?: Date }) => {
				return BaseConverter.convert<PaymentAccountMetricsModel>(
					await actions.get(API_URLS.SOCCER.GET_PAYMENT_ACCOUNT_METRICS, params, {
						resolvers: [
							{
								resolver: paymentAccountMetricsResolver
							}
						]
					})
				);
			},
			refundForOrder: async (params: { orderId: string; amount: number }) => {
				return BaseConverter.convert<{ order: { id: string; refundedAmount: number } }>(
					await actions.post(API_URLS.SOCCER.REFUND_FOR_ORDER, params)
				);
			},
			removePaymentRequest: async (id: string) => {
				return BaseConverter.convert(
					await actions.post(API_URLS.SOCCER.REMOVE_PAYMENT_REQUEST, {
						id
					})
				);
			},
			getPendingOrders: async (
				params: {
					objectId: string;
					objectType: BackendEntityType;
					linkedObjectId?: string;
				} & PaginationParamsModel
			) => {
				return BaseConverter.convert<{
					nextToken: PaginationTokenType;
					orders: PendingPaymentRequestOrderModel[];
				}>(
					await actions.get(API_URLS.SOCCER.LIST_PENDING_ORDERS, params, {
						resolvers: [
							{
								resolver: pendingPaymentRequestOrderResolver,
								fields: ["orders"]
							},
							{
								resolver: paginationTokenResolver,
								fields: ["nextToken"]
							}
						]
					})
				);
			},
			checkStripeConnectIntegrationStatus: async (params: { objectId: string }) => {
				return BaseConverter.convert<StripeConnectAccountIntegrationResponse>(
					await actions.get(API_URLS.SOCCER.CHECK_STRIPE_CONNECT_ACCOUNT_INTEGRATION, params, {
						resolvers: [
							{
								resolver: stripeConnectAccountIntegrationResponseResolver
							}
						]
					})
				);
			},
			registerStripeConnectAccount: async (params: {
				objectId: string;
				objectType: BackendEntityType;
				countryCode: string;
				refreshLink: string;
				successLink: string;
			}) => {
				return BaseConverter.convert<{ link: string }>(
					await actions.post(API_URLS.SOCCER.REGISTER_STRIPE_CONNECT_ACCOUNT, params)
				);
			},
			listPaymentOrgs: async (
				params: {
					search?: string;
				} & PaginationParamsModel
			) => {
				return BaseConverter.convert<ListPaymentOrgInfoResponse>(
					await actions.get(API_URLS.SOCCER.LIST_PAYMENT_ORGS, params, {
						resolvers: [
							{
								resolver: listPaymentOrgInfoResolver
							}
						]
					})
				);
			},
			updateOrderSubscriptionSettings: async (params: {
				orderId: string;
				payAsEntityId?: string;
				payAsEntityType?: BackendEntityType;
				subscription: null | PartialPaymentModel;
			}) => {
				return BaseConverter.convert<{ order: Partial<PaymentRequestOrderBaseModal> }>(
					await actions.post(API_URLS.SOCCER.UPDATE_ORDER_SUBSCRIPTION, params, {
						resolvers: [
							{
								fields: ["order"],
								resolver: paymentRequestOrderBaseResolver.partial()
							}
						]
					})
				);
			},
			markPaymentRequestAsPaidExternally: async (paymentRequestId: string) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.SOCCER.MARK_PAYMENT_REQUEST_PAID_EXTERNALLY, {
						id: paymentRequestId
					})
				);
			}
		}),
		[actions]
	);
};

export default useSoccerPaymentApiService;
